import React, { useState } from 'react';
import { Button, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { useAppContext } from '../../contexts/AppContext';
import circle from '@turf/circle';
import { point } from '@turf/helpers';

export default function NewStationForm() {
  const {
    newStation, setNewStation,
    fetchWithAuth,
    mapRef,
    setSelectedProject,
    setSidebarOpen,
    addToast,
  } = useAppContext();

  const [newPdNummer, setNewPdNummer] = useState('');
  const [bezig, setBezig] = useState(false);

  if (!newStation) return null;

  const opslaan = async () => {
    setBezig(true);
    try {
      const resp = await fetchWithAuth('/api/projecten/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectnummer: newPdNummer,
          type: 'LS – Netstation Nieuwbouw',
          soort_project: 'UITVOERING',
          buurtnaam: newStation.buurtnaam,
          buurtcode: newStation.buurtcode,
          locatie: [newStation.lng, newStation.lat],
        }),
      });
      if (resp.ok) {
        const newProjectData = await resp.json();
        addToast('Station toegevoegd', 'success');
        setNewStation(null);

        // Herlaad beide kaartlagen exact zoals in drillDown
        if (newStation.buurtcode) {
          const map = mapRef.current;
          const buurtCode = newStation.buurtcode;

          const respProj = await fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(buurtCode)}`);
          const data = await respProj.json();
          const stippenData = { type: 'FeatureCollection', features: [] };
          if (data && data.features && data.features.length) {
            data.features.forEach((proj, idx) => {
              if (
                proj.properties.soort_project !== 'UITVOERING' || (
                proj.properties.type !== null &&
                !proj.properties.type.toLowerCase().includes('station'))
              ) return;
              if (!proj.geometry || !proj.geometry.coordinates) return;
              const coord = proj.geometry.coordinates;
              const key = proj.properties.locatie || idx;
              const pid = proj.properties.projectnummer;
              const type = proj.properties.type || '';
              const isTransparant = !type || !type.toLowerCase().includes('station');
              const rotation = typeof proj.properties.rotatie1 === 'number' ? proj.properties.rotatie1 : 0;
              const p = {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: coord },
                properties: {
                  ...proj.properties,
                  featuretype: 'stip',
                  id: pid,
                  locatie: key,
                  transparant: isTransparant,
                  rotatie1: rotation,
                },
              };
              const c = circle(coord, 0.1, { steps: 64, units: 'kilometers' });
              c.properties = { featuretype: 'circle', id: pid };
              stippenData.features.push(p, c);
            });
          }
          map.getSource('projecten').setData(stippenData);

          const resp2 = await fetchWithAuth(`/api/laagspanningsstations?buurtcode=${encodeURIComponent(buurtCode)}`);
          const data2 = await resp2.json();
          const stippenData2 = { type: 'FeatureCollection', features: [] };
          if (data2 && data2.features && data2.features.length) {
            data2.features.forEach((station, idx) => {
              if (!station.geometry || !station.geometry.coordinates) return;
              const coord = station.geometry.coordinates;
              const key = station.properties.id || idx;
              const pid = `lsstation_${key}`;
              const p = point(coord, { featuretype: 'stip', id: pid, ...station.properties });
              const c = circle(coord, 0.1, { steps: 64, units: 'kilometers' });
              c.properties = { featuretype: 'circle', id: pid };
              stippenData2.features.push(p, c);
            });
          }
          map.getSource('laagspanningsstations').setData(stippenData2);

          // Zoek en selecteer het nieuwe project
          if (newProjectData && newProjectData.projectnummer) {
            const newProject = data.features.find(f =>
              f.properties.projectnummer === newProjectData.projectnummer
            );
            if (newProject) {
              setSelectedProject(newProject.properties);
              setSidebarOpen(true);
            }
          }
        }
      } else {
        addToast('Fout bij opslaan station', 'error');
      }
    } catch (e) {
      addToast('Fout bij opslaan station', 'error');
    } finally {
      setBezig(false);
    }
  };

  return (
    <Stack gap="sm">
      <Title order={4}>Nieuw station plaatsen</Title>
      <Text size="sm" c="dimmed">
        Locatie: {newStation.lng.toFixed(5)}, {newStation.lat.toFixed(5)}
      </Text>
      <TextInput label="Buurtcode" value={newStation.buurtcode || ''} readOnly />
      <TextInput
        label="PD-nummer"
        placeholder="Voer PD-nummer in"
        value={newPdNummer}
        onChange={e => setNewPdNummer(e.currentTarget.value)}
        autoFocus
      />
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={() => setNewStation(null)}>Annuleren</Button>
        <Button onClick={opslaan} loading={bezig} disabled={!newPdNummer || !newStation.buurtcode}>
          Opslaan
        </Button>
      </Group>
    </Stack>
  );
}
